import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listTrips } from '../lib/api';
import type { Trip } from '../lib/types';
import { WatercolorBloom } from '../components/WatercolorBloom';
import { IconArrowRight } from '../components/Icons';

const MONTH_COUNT = 13; // current month + 12 forward

const COVER_GRADIENT: Record<string, string> = {
  neon_dream:        'linear-gradient(90deg, rgba(255,43,214,0.95), rgba(123,92,255,0.95), rgba(0,240,255,0.95))',
  electric_horizon:  'linear-gradient(90deg, rgba(0,240,255,0.95),  rgba(167,255,42,0.95), rgba(255,214,0,0.95))',
  night_pulse:       'linear-gradient(90deg, rgba(13,16,38,0.95),    rgba(123,92,255,0.95), rgba(255,43,214,0.95))',
  holographic_bloom: 'linear-gradient(90deg, rgba(123,92,255,0.95), rgba(255,43,214,0.95), rgba(0,240,255,0.95))',
};

const STATUS_PILL: Record<Trip['status'], string> = {
  idea:        'rgba(232,217,255,0.9)',
  researching: 'rgba(0,240,255,0.9)',
  planning:    'rgba(255,214,0,0.9)',
  booked:      'rgba(255,43,214,0.95)',
  in_progress: 'rgba(167,255,42,0.95)',
  completed:   'rgba(75,79,115,0.7)',
  archived:    'rgba(75,79,115,0.5)',
};

interface Month { year: number; month: number; key: string; short: string; firstDay: Date; lastDay: Date; }

function buildMonths(from: Date): Month[] {
  const out: Month[] = [];
  const y0 = from.getFullYear(); const m0 = from.getMonth();
  for (let i = 0; i < MONTH_COUNT; i++) {
    const first = new Date(y0, m0 + i, 1);
    const last  = new Date(y0, m0 + i + 1, 0);
    out.push({
      year:  first.getFullYear(),
      month: first.getMonth(),
      key:   `${first.getFullYear()}-${String(first.getMonth()+1).padStart(2,'0')}`,
      short: first.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      firstDay: first,
      lastDay:  last,
    });
  }
  return out;
}

function formatBarRange(s?: string | null, e?: string | null): string {
  if (!s) return '';
  const f = (d: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
    if (!m) return d;
    const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  if (e && e !== s) return `${f(s)} → ${f(e)}`;
  return f(s);
}

function parseLocalDate(d: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(d);
}

interface LaneTrip extends Trip { startPct: number; widthPct: number; clipLeft: boolean; clipRight: boolean; lane: number; }

export default function Calendar() {
  const [all, setAll] = useState<Trip[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    listTrips().then(setAll).catch(e => setErr(e?.message ?? 'load failed'));
  }, []);

  const today = new Date(); today.setHours(0,0,0,0);
  const months = useMemo(() => buildMonths(today), []);
  const windowStart = months[0].firstDay;
  const windowEnd   = new Date(months[months.length-1].lastDay); windowEnd.setHours(23,59,59,999);
  const totalMs     = windowEnd.getTime() - windowStart.getTime();
  const todayPct    = ((today.getTime() - windowStart.getTime()) / totalMs) * 100;

  const { dated, undated } = useMemo(() => {
    const ds: LaneTrip[] = []; const ud: Trip[] = [];
    for (const t of all ?? []) {
      if (!t.start_date) { ud.push(t); continue; }
      const s = parseLocalDate(t.start_date);
      const e = t.end_date ? parseLocalDate(t.end_date) : s;
      // Filter: skip trips that ended before the window started
      if (e.getTime() < windowStart.getTime()) continue;
      // Skip trips entirely past window end
      if (s.getTime() > windowEnd.getTime()) continue;
      const clipLeft  = s.getTime() < windowStart.getTime();
      const clipRight = e.getTime() > windowEnd.getTime();
      const startMs = Math.max(s.getTime(), windowStart.getTime());
      const endMs   = Math.min(e.getTime() + 86399999, windowEnd.getTime());
      const startPct = ((startMs - windowStart.getTime()) / totalMs) * 100;
      const widthPct = Math.max(2, ((endMs - startMs) / totalMs) * 100);
      ds.push({ ...t, startPct, widthPct, clipLeft, clipRight, lane: 0 });
    }
    ds.sort((a, b) => a.startPct - b.startPct);
    ds.forEach((t, i) => { t.lane = i; });  // one trip per row, ordered by start
    return { dated: ds, undated: ud };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all]);

  const lanes = Math.max(1, dated.length);
  const laneH = 60;
  const chartHeight = lanes * laneH + 16;

  return (
    <div className="col" style={{ gap: 24 }}>
      <section className="card card-dark brackets" style={{ position: 'relative', overflow: 'hidden', padding: 28 }}>
        <WatercolorBloom variant="duo" size={400} opacity={0.45} style={{ position: 'absolute', top: -140, right: -100 }} />
        <WatercolorBloom variant="uv"  size={360} opacity={0.35} style={{ position: 'absolute', bottom: -160, left: -80 }} />
        <div style={{ position: 'relative' }}>
          <span className="label" style={{ color: 'var(--c-cyan)' }}>// MISSION TIMELINE · YEAR FORWARD</span>
          <h1 className="h1" style={{ marginTop: 8, color: 'white' }}>
            <span className="gradient-text">{months[0].short} {months[0].year}</span>
            <span style={{ color: 'rgba(232,217,255,0.5)', margin: '0 16px' }}>→</span>
            <span className="gradient-text">{months[months.length-1].short} {months[months.length-1].year}</span>
          </h1>
          <p className="body" style={{ color: 'var(--c-lavender)', maxWidth: 540, marginTop: 12 }}>
            {dated.length} dated trip{dated.length===1?'':'s'} across {MONTH_COUNT} months · {undated.length} undated idea{undated.length===1?'':'s'} in the queue.
          </p>
        </div>
      </section>

      {err && <div className="card" style={{ borderColor: '#FF2BD6' }}><span className="label-bright">Error</span> {err}</div>}

      <section className="card" style={{ overflow: 'auto', padding: 0 }}>
        <div style={{ minWidth: 1100 }}>
          {/* Month header strip */}
          <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: `220px repeat(${MONTH_COUNT}, 1fr)`, borderBottom: '1px solid var(--c-border-soft)' }}>
            <div style={{ padding: '14px 18px', background: 'var(--c-mist)' }}>
              <span className="label">// TRIP</span>
            </div>
            {months.map((m, i) => (
              <div key={m.key} style={{
                padding: '14px 8px',
                textAlign: 'center',
                borderLeft: '1px solid var(--c-border-soft)',
                background: m.month === 0 || i === 0 ? 'rgba(123,92,255,0.06)' : 'transparent',
              }}>
                <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 12, letterSpacing: '0.08em', color: 'var(--c-ink)' }}>{m.short}</div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 11, color: 'var(--c-muted-ink)', marginTop: 2 }}>'{String(m.year).slice(2)}</div>
              </div>
            ))}
          </div>

          {/* Chart area */}
          <div style={{ display: 'grid', gridTemplateColumns: `220px 1fr`, position: 'relative' }}>
            {/* Trip-title gutter — one row per trip */}
            <div style={{ borderRight: '1px solid var(--c-border-soft)', background: 'var(--c-mist)' }}>
              {dated.length === 0 ? (
                <div style={{ height: laneH }} />
              ) : dated.map((t, i) => (
                <Link key={t.id} to={`/trip/${t.id}`} className="col"
                      style={{ gap: 2, height: laneH, padding: '12px 14px',
                               borderTop: i === 0 ? 'none' : '1px solid var(--c-border-soft)',
                               color: 'inherit', textDecoration: 'none', overflow: 'hidden' }}>
                  <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 12,
                                 color: 'var(--c-ink)', letterSpacing: '0.02em',
                                 whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {t.pinned && <span style={{ color: '#FFD600' }}>★ </span>}{t.title}
                  </span>
                  <span className="label muted" style={{ fontSize: 10 }}>
                    {t.status.replace('_',' ').toUpperCase()}
                    {' · '}{formatBarRange(t.start_date, t.end_date)}
                  </span>
                </Link>
              ))}
            </div>

            {/* Bars area */}
            <div style={{ position: 'relative', height: chartHeight, background:
              `repeating-linear-gradient(90deg, transparent 0, transparent calc(100% / ${MONTH_COUNT} - 1px), rgba(123,92,255,0.10) calc(100% / ${MONTH_COUNT} - 1px), rgba(123,92,255,0.10) calc(100% / ${MONTH_COUNT}))`
            }}>
              {/* Today marker */}
              {todayPct >= 0 && todayPct <= 100 && (
                <div style={{
                  position: 'absolute', left: `${todayPct}%`, top: 0, bottom: 0,
                  width: 2, background: 'linear-gradient(180deg, #FF2BD6, #00F0FF)',
                  boxShadow: '0 0 16px rgba(255,43,214,0.8)', pointerEvents: 'none', zIndex: 5,
                }}>
                  <span style={{
                    position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)',
                    background: '#0D1026', color: '#00F0FF',
                    padding: '2px 8px', borderRadius: 6,
                    fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 9, letterSpacing: '0.1em',
                    whiteSpace: 'nowrap',
                    border: '1px solid rgba(0,240,255,0.6)',
                  }}>TODAY</span>
                </div>
              )}

              {/* Horizontal divider lines between trip rows */}
              {dated.map((_, i) => i === 0 ? null : (
                <div key={`div-${i}`} style={{
                  position: 'absolute', left: 0, right: 0, top: i * laneH, height: 1,
                  background: 'var(--c-border-soft)', pointerEvents: 'none', zIndex: 1,
                }} />
              ))}

              {/* Trip bars */}
              {dated.map(t => (
                <Link
                  key={t.id} to={`/trip/${t.id}`}
                  style={{
                    position: 'absolute',
                    top: t.lane * laneH + 14,
                    left:  `${t.startPct}%`,
                    width: `${t.widthPct}%`,
                    minWidth: 24,
                    height: laneH - 28,
                    background: COVER_GRADIENT[t.cover_color ?? 'neon_dream'] ?? COVER_GRADIENT.neon_dream,
                    borderRadius: 8,
                    boxShadow: '0 4px 16px rgba(123,92,255,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 12px',
                    gap: 8,
                    color: 'white',
                    textDecoration: 'none',
                    overflow: 'hidden',
                    border: t.pinned ? '1.5px solid rgba(255,255,255,0.9)' : '1px solid rgba(255,255,255,0.4)',
                    transition: 'transform var(--t-fast) var(--ease), box-shadow var(--t-fast) var(--ease)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(123,92,255,0.5)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)';   e.currentTarget.style.boxShadow = '0 4px 16px rgba(123,92,255,0.3)'; }}
                  title={`${t.title} · ${t.start_date} → ${t.end_date ?? t.start_date}`}
                >
                  {t.clipLeft && <span style={{ fontFamily: 'var(--font-display)' }}>←</span>}
                  {t.pinned && <span style={{ color: '#FFD600', textShadow: '0 0 8px rgba(255,214,0,0.8)' }}>★</span>}
                  <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 12, letterSpacing: '0.04em', textShadow: '0 1px 3px rgba(0,0,0,0.4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {t.title}
                  </span>
                  {t.clipRight && <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-display)' }}>→</span>}
                </Link>
              ))}

              {/* Empty state */}
              {dated.length === 0 && all !== null && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span className="label muted">// No dated trips in the next 12 months</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Undated trips */}
      {undated.length > 0 && (
        <section className="card brackets" style={{ position: 'relative', overflow: 'hidden', padding: 24 }}>
          <WatercolorBloom variant="lime" size={220} opacity={0.3} style={{ position: 'absolute', top: -60, right: -50 }} />
          <div style={{ position: 'relative' }}>
            <span className="label-bright">// UNDATED IDEAS</span>
            <h2 className="h3" style={{ marginTop: 6 }}>Floating in the queue — drop dates to put them on the timeline.</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: 12, marginTop: 14 }}>
              {undated.map(t => (
                <Link key={t.id} to={`/trip/${t.id}`} className="card" style={{ padding: 14, color: 'inherit', textDecoration: 'none' }}>
                  <span className="label">{t.status.replace('_',' ').toUpperCase()}</span>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 15, lineHeight: 1.15, textTransform: 'uppercase', marginTop: 6 }}>{t.title}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <Link to="/" className="btn btn-ghost">← Back to trips</Link>
        <Link to="/new" className="btn btn-primary">Sketch a new journey <IconArrowRight size={14} /></Link>
      </div>
    </div>
  );
}
